/**
 * Theme switcher controller supporting System (default), Light, Dark, Sepia, and Midnight
 */

export const THEME_STORAGE_KEY = "ghost-theme-mode";

export const THEMES = {
    system: { label: "System", iconClass: "dot-system" },
    light: { label: "Light", iconClass: "dot-light" },
    dark: { label: "Dark", iconClass: "dot-dark" },
    sepia: { label: "Sepia", iconClass: "dot-sepia" },
    midnight: { label: "Midnight", iconClass: "dot-midnight" }
};

export function getSavedTheme() {
    try {
        return localStorage.getItem(THEME_STORAGE_KEY) || "system";
    } catch {
        return "system";
    }
}

export function applyTheme(theme) {
    const root = document.documentElement;
    const activeTheme = THEMES[theme] ? theme : "system";

    if (activeTheme === "system") {
        root.removeAttribute("data-theme");
    } else {
        root.setAttribute("data-theme", activeTheme);
    }

    try {
        localStorage.setItem(THEME_STORAGE_KEY, activeTheme);
    } catch {
        // Storage might be disabled
    }

    updateUI(activeTheme);
}

export function updateUI(currentTheme) {
    const switchers = document.querySelectorAll("[data-theme-switcher]");
    if (!switchers.length) return;

    const themeInfo = THEMES[currentTheme] || THEMES.system;

    switchers.forEach((switcher) => {
        const labelEl = switcher.querySelector(".js-theme-current-label");
        const iconContainer = switcher.querySelector(".js-theme-current-icon");
        const options = switcher.querySelectorAll(".js-theme-option");

        if (labelEl) {
            labelEl.textContent = themeInfo.label;
        }

        if (iconContainer) {
            if (currentTheme === "system") {
                iconContainer.innerHTML = `
                    <svg class="icon-theme-system" viewBox="0 0 24 24" width="16" height="16">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor"></path>
                    </svg>
                `;
            } else if (currentTheme === "light") {
                iconContainer.innerHTML = `
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </svg>
                `;
            } else if (currentTheme === "dark") {
                iconContainer.innerHTML = `
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </svg>
                `;
            } else if (currentTheme === "sepia") {
                iconContainer.innerHTML = `
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                    </svg>
                `;
            } else if (currentTheme === "midnight") {
                iconContainer.innerHTML = `
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                `;
            }
        }

        options.forEach((opt) => {
            const val = opt.getAttribute("data-theme-value");
            if (val === currentTheme) {
                opt.classList.add("is-active");
                opt.setAttribute("aria-selected", "true");
            } else {
                opt.classList.remove("is-active");
                opt.removeAttribute("aria-selected");
            }
        });
    });
}

export default function initThemeToggle() {
    const initialTheme = getSavedTheme();
    applyTheme(initialTheme);

    const switchers = document.querySelectorAll("[data-theme-switcher]");
    if (!switchers.length) return;

    switchers.forEach((switcher) => {
        const btn = switcher.querySelector(".js-theme-btn");
        const dropdown = switcher.querySelector(".js-theme-dropdown");
        const options = switcher.querySelectorAll(".js-theme-option");

        if (!btn || !dropdown) return;

        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const isOpen = switcher.classList.toggle("is-open");
            btn.setAttribute("aria-expanded", isOpen ? "true" : "false");
        });

        options.forEach((option) => {
            option.addEventListener("click", (e) => {
                e.stopPropagation();
                const themeVal = option.getAttribute("data-theme-value");
                if (themeVal) {
                    applyTheme(themeVal);
                }
                switcher.classList.remove("is-open");
                btn.setAttribute("aria-expanded", "false");
            });
        });
    });

    // Close on click outside or Escape
    document.addEventListener("click", (e) => {
        switchers.forEach((switcher) => {
            if (!switcher.contains(e.target)) {
                switcher.classList.remove("is-open");
                const btn = switcher.querySelector(".js-theme-btn");
                if (btn) btn.setAttribute("aria-expanded", "false");
            }
        });
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            switchers.forEach((switcher) => {
                switcher.classList.remove("is-open");
                const btn = switcher.querySelector(".js-theme-btn");
                if (btn) btn.setAttribute("aria-expanded", "false");
            });
        }
    });

    // Listen to system color scheme changes when system mode is selected
    if (window.matchMedia) {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        mediaQuery.addEventListener("change", () => {
            if (getSavedTheme() === "system") {
                updateUI("system");
            }
        });
    }
}
